import { useGetWarrantyProvidersQuery } from '@/services/warranties';
import {
  Box,
  Button,
  FormikCheckboxInput,
  FormikSelectInputNative,
  FormikTextInput,
  Modal,
} from '@palmetto/palmetto-components';
import { Field, FieldArray, Form, Formik, FormikValues } from 'formik';
import { SyntheticEvent, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { ProgramType, warrantyMap, WarrantyPaymentFrequency, WarrantyType } from 'types';

export const ZoningWarrantyModal = ({
  isModalOpen,
  closeModal,
  warrantyType,
  onSubmit,
}: {
  isModalOpen: boolean;
  closeModal: (event?: SyntheticEvent) => void;
  warrantyType?: string;
  onSubmit: (values: FormikValues) => void;
}) => {
  const { programType = ProgramType.hvac } = useParams<{ programType: string }>();
  const warranty = warrantyMap[programType as ProgramType][warrantyType as WarrantyType];
  const { data: warrantyProviderResponse } = useGetWarrantyProvidersQuery({});

  const warrantyProviderOptions = useMemo(() => {
    const programTypeProviders = warrantyProviderResponse?.data?.find(
      (item) => item.programType === programType,
    )?.providers;

    const warrantyProviderList = programTypeProviders?.map((p) => p.name) || [];
    return warrantyProviderList.map((element) => {
      return {
        label: element,
        value: element,
      };
    });
  }, [programType, warrantyProviderResponse]);
  return (
    <Modal ariaLabeledBy="createAccessoryWarranty" isOpen={isModalOpen} onDismiss={closeModal} maxWidth="1000px">
      <Modal.Header id="createAccessoryWarranty" title={`Add ${warranty}`} onDismiss={closeModal} />
      <Formik
        initialValues={{
          warrantyProvider: '',
          paymentFrequency: WarrantyPaymentFrequency.upfront,
          zoning: [
            {
              minZones: 0,
              maxZones: 0,
              warrantyCost: 0,
              perZone: false,
            },
          ],
        }}
        onSubmit={({ warrantyProvider, paymentFrequency, zoning }) =>
          onSubmit({
            warrantyProvider,
            paymentFrequency,
            warrantyType: WarrantyType.zoning,
            zoning,
          })
        }
      >
        {({ isSubmitting, values }) => {
          return (
            <Form>
              <Modal.Body>
                <Box direction="column" gap="md">
                  <Field
                    type="select"
                    component={FormikSelectInputNative}
                    label="Warranty Provider"
                    name="warrantyProvider"
                    id="warrantyProvider"
                    options={warrantyProviderOptions}
                    isRequired
                  />
                  <Field
                    type="select"
                    component={FormikSelectInputNative}
                    label="Payment Frequency"
                    name="paymentFrequency"
                    id="paymentFrequency"
                    options={Object.keys(WarrantyPaymentFrequency).map((key) => ({
                      label: WarrantyPaymentFrequency[key as keyof typeof WarrantyPaymentFrequency],
                      value: key,
                    }))}
                    isRequired
                  />
                  <Box direction="column" gap="md">
                    <FieldArray
                      name="zoning"
                      render={(arrayHelpers) => {
                        return (
                          <Box gap="lg">
                            {values.zoning.map((_warranty: any, index: number) => (
                              <Box gap="lg" key={index}>
                                <Box gap="md" direction="row" alignItems="center">
                                  <Field
                                    type="text"
                                    component={FormikTextInput}
                                    label="Min Zones"
                                    name={`zoning.${index}.minZones`}
                                    id={`zoning.${index}.minZones`}
                                    value={values.zoning[index].minZones}
                                  />
                                  <Field
                                    type="text"
                                    component={FormikTextInput}
                                    label="Max Zones"
                                    name={`zoning.${index}.maxZones`}
                                    id={`zoning.${index}.maxZones`}
                                    value={values.zoning[index].maxZones}
                                  />
                                  <Field
                                    type="text"
                                    component={FormikTextInput}
                                    label="Warranty Cost"
                                    name={`zoning.${index}.warrantyCost`}
                                    id={`zoning.${index}.warrantyCost`}
                                    value={values.zoning[index].warrantyCost}
                                  />
                                  <Field
                                    type="text"
                                    component={FormikCheckboxInput}
                                    label="Per Zone"
                                    name={`zoning.${index}.perZone`}
                                    id={`zoning.${index}.perZone`}
                                    value={values.zoning[index].perZone}
                                  />
                                </Box>
                              </Box>
                            ))}
                            <Button
                              as="button"
                              onClick={() =>
                                arrayHelpers.push({
                                  minZones: 0,
                                  maxZones: 0,
                                  warrantyCost: 0,
                                  perZone: false,
                                })
                              }
                              variant="secondary"
                              tone="neutral"
                              size="sm"
                              aria-label="Add Additional Item"
                              iconPrefix="add"
                            >
                              Add Warranty Band
                            </Button>
                          </Box>
                        );
                      }}
                    />
                  </Box>
                </Box>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  as="button"
                  onClick={() => {
                    closeModal();
                  }}
                  variant="secondary"
                  tone="neutral"
                  size="md"
                  isLoading={isSubmitting}
                >
                  Cancel
                </Button>
                <Button size="md" type="submit" isLoading={isSubmitting}>
                  Add Option
                </Button>
              </Modal.Footer>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};
